import { useEffect, useState } from "react";
import { Client } from "appwrite";
import { appwriteConfig, functions } from "@/lib/appwrite/config";
import {
  createDealStatus,
  getDealByBuyerAndSeller,
  updateDealStatus,
} from "@/lib/appwrite/api";
import { DealDocument } from "@/types";
import { notificationService } from "@/services/NotificationService";
import { createDealPaymentOrder, verifyCashfreePayment } from "@/lib/cashfree/paymentService";
import { INITIAL_USER, useUserContext } from "@/context/AuthContext";
import { toast } from "@/components/ui";
import { useNavigate } from "react-router-dom";
import { getUserTrustScore, logTrustScoreEvent } from "@/lib/appwrite/trustScore";
import { X, Shield, Wallet, CreditCard, AlertCircle, Loader, RefreshCw, Crown } from 'lucide-react';
import { isDeliveryDetailsBlocked, violationTracker } from "@/lib/utils/blockedPatterns";
import { getViolationType, isUserBanned, recordUserViolation } from "@/lib/appwrite/userViolationAPI";
import { useSignOutAccount } from "@/lib/react-query/queries";

interface DealTrackerProps {
  buyerId: string;
  sellerId: string;
  currentUserId: string;
  isCollapsible?: boolean;
}

interface PaymentBreakdown {
  originalAmount: number;
  discount: number;
  platformFee: number;
  platformFeePercent: number;
  finalAmount: number;
  canApplyDiscount: boolean;
  maxTrustDiscountAvailable: number;
  trustScorePoints: number;
}

declare global {
  interface Window {
    Cashfree: any;
  }
}

export const DealTracker = ({ 
  buyerId, 
  sellerId, 
  currentUserId, 
  isCollapsible = false
}: DealTrackerProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [deal, setDeal] = useState<DealDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<"buyer" | "seller">("buyer");
  const [isUpdating, setIsUpdating] = useState(false);

  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const { user } = useUserContext();
  const [isReleasingPayment, setIsReleasingPayment] = useState(false);
  const navigate = useNavigate();

  // New state for payment modal
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentBreakdown, setPaymentBreakdown] = useState<PaymentBreakdown | null>(null);
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState({
    address: '',
    mobile: '',
    landmark: ''
  });

      const { setUser, setIsAuthenticated } = useUserContext();
    const { mutate: signOut } = useSignOutAccount();
  const [isSubmittingAddress, setIsSubmittingAddress] = useState(false);
const [showReviseDialog, setShowReviseDialog] = useState(false);
const [buyerTrustScore, setBuyerTrustScore] = useState<number | null>(null);
const [isCheckingTrustScore, setIsCheckingTrustScore] = useState(false);

  useEffect(() => {
    const client = new Client()
      .setEndpoint(appwriteConfig.url)
      .setProject(appwriteConfig.projectId);

    let unsubscribe: () => void;
    let isMounted = true;

    const initDeal = async () => {
      try {
        setLoading(true);

        let existingDeal =
          (await getDealByBuyerAndSeller(buyerId, sellerId)) ||
          (await getDealByBuyerAndSeller(sellerId, buyerId));

        if (existingDeal?.wasInitiated) {
          if (isMounted) {
            setDeal(existingDeal);
            setRole(currentUserId === existingDeal.buyerId ? "buyer" : "seller");
          }

          unsubscribe = client.subscribe(
            `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.dealStage}.documents.${existingDeal.$id}`,
            (response: any) => {
              if (response.payload && isMounted) {
                setDeal(response.payload);
              }
            }
          );
          return;
        }

        const newDeal = await createDealStatus({
          buyerId,
          sellerId,
          wasInitiated: true,
        });

        if (isMounted && newDeal) {
          setDeal(newDeal);
          setRole(currentUserId === newDeal.buyerId ? "buyer" : "seller");

          unsubscribe = client.subscribe(
            `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.dealStage}.documents.${newDeal.$id}`,
            (response: any) => {
              if (response.payload && isMounted) {
                setDeal(response.payload);
              }
            }
          );
        }
      } catch (error) {
        console.error("Error initializing deal:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const timer = setTimeout(() => {
      initDeal();
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      if (unsubscribe) unsubscribe();
    };
  }, [buyerId, sellerId, currentUserId]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (
      deal?.status === "delivered" && 
      currentUserId === deal?.buyerId &&
      deal?.disputeStatus !== "pending"
    ) {
      const deliveryTime = new Date(deal.updatedAt).getTime();
      const expiryTime = deliveryTime + 24 * 60 * 60 * 1000;
    
      const updateTimeLeft = () => {
        const now = Date.now();
        const timeDiff = Math.max(0, expiryTime - now);
        setTimeLeft(timeDiff);
    
        if (timeDiff === 0) {
          handleStatusUpdate("completed");
        }
      };
    
      updateTimeLeft();
      interval = setInterval(updateTimeLeft, 10000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [deal?.status, deal?.updatedAt, deal?.disputeStatus, currentUserId, deal?.buyerId]);

  const checkReviseEligibility = async () => {
  if (!isBuyer || !deal || deal.status !== "paid") return;
  
  setIsCheckingTrustScore(true);
  try {
    const trustData = await getUserTrustScore(buyerId);
    setBuyerTrustScore(trustData?.score || 0);
    
    if (trustData?.score >= 5000) {
      // Automatically revert to negotiation
      await handleReviseDeal(trustData.score);
    } else {
      // Show upgrade modal
      setShowReviseDialog(true);
    }
  } catch (error) {
    console.error("Error checking trust score:", error);
    toast({
      title: "Error",
      description: "Could not check eligibility. Please try again.",
      variant: "destructive"
    });
  } finally {
    setIsCheckingTrustScore(false);
  }
};

// Handle automatic deal revision for eligible users
const handleReviseDeal = async (trustScore: number) => {
  if (!deal || trustScore < 5000) return;

  try {
    // Revert deal status to negotiation and set isDealRevised flag
    await updateDealStatus(deal.$id, {
      status: "negotiation",
      amount: "0",
      requestedAmount: "0",
      isDealRevised: true,
      revisedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setDeal(prev => prev ? {...prev, status: "negotiation",amount:"0",requestedAmount: "0", isDealRevised: true, revisedAt: new Date().toISOString()} : null);
    // Notify seller about revision
    await notificationService.createNotification({
      title: "Deal Revised",
      body: `Buyer has revised the deal for ₹${deal.amount}. You can now renegotiate the terms.`,
      type: "deal",
      recipient_id: sellerId,
      sender_id: buyerId,
    });
    
    toast({
      title: "Deal Revised Successfully",
      description: "You can now renegotiate the terms with the seller.",
    });

  } catch (error) {
    console.error("Error revising deal:", error);
    toast({
      title: "Error",
      description: "Could not revise deal. Please try again.",
      variant: "destructive"
    });
  }
};


const validateDeliveryAddress = async (address: any) => {
  const errors = [];
  // Check if user is banned first
  try {
    const userIsBanned = await isUserBanned(currentUserId);
    if (userIsBanned) {
      toast({
        title: "Account Suspended",
        description: "Your account has been suspended due to repeated violations of our community guidelines. Contact contact@busiconn.com for assistance.",
        variant: "destructive"
      });
      signOut();
      setIsAuthenticated(false);
      setUser(INITIAL_USER);
      navigate("/sign-in?banned=true");
      return ["Account suspended"];
    }
  } catch (error) {
    console.error('Error checking ban status:', error);
  }
  
  // Check for email patterns in address and landmark fields
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
  
  if (emailRegex.test(address.address)) {
    errors.push("Email addresses are not allowed in address field");
  }
  if (emailRegex.test(address.landmark)) {
    errors.push("Email addresses are not allowed in landmark field");
  }
  
  // Check for mobile numbers in address field using existing violation tracker
  const addressBlockResult = isDeliveryDetailsBlocked(address.address || '');
  if (addressBlockResult.isBlocked && isBuyer) {
    try {
      // Record the violation in the database
      const violationType = getViolationType(addressBlockResult.reason);
      const violationResult = await recordUserViolation(currentUserId, {
        violationType,
        messageText: address.address,
        severity: addressBlockResult.severity
      });
      
      const { violation } = violationResult;
      
      // Determine appropriate message based on violation stage
      let title = "Invalid Address Format";
      let description = "Mobile numbers are not allowed in address field. Use the mobile field instead.";
      
      switch (violation.violationStage) {
        case 'banned':
          title = "Account Suspended";
          description = "Your account has been suspended due to repeated violations. Contact support if you believe this is an error.";
          break;
          
        case 'flagged':
          title = "Account Flagged";
          const flagDate = violation.flaggedUntil ? new Date(violation.flaggedUntil).toLocaleDateString('en-GB') : 'N/A';
          description = `Mobile numbers are not allowed in address field. Your account is now flagged until ${flagDate}. Further violations will result in account suspension. (Total violations: ${violation.violationCount})`;
          break;
          
        default: // 'normal'
          if (addressBlockResult.severity === 'violation') {
            title = "Content Violation";
            description = `Mobile numbers are not allowed in address field. This violation has been recorded. (Total violations: ${violation.violationCount})`;
          } else {
            title = "Content Warning";
            description = `Mobile numbers are not allowed in address field. Please follow our community guidelines.`;
          }
      }
      
      toast({
        title,
        description,
        variant: "destructive"
      });
      
    } catch (dbError) {
      console.error('Error recording violation to database:', dbError);
      
    }
    
    errors.push("Mobile numbers are not allowed in address field. Use the mobile field instead.");
  }
  
  // Check for mobile numbers in landmark field using existing violation tracker
  const landmarkBlockResult = isDeliveryDetailsBlocked(address.landmark || '');
  if (landmarkBlockResult.isBlocked && isBuyer) {
    try {
      // Record the violation in the database
      const violationType = getViolationType(landmarkBlockResult.reason);
      const violationResult = await recordUserViolation(currentUserId, {
        violationType,
        messageText: address.landmark,
        severity: landmarkBlockResult.severity
      });
      
      const { violation } = violationResult;
      
      // Determine appropriate message based on violation stage
      let title = "Invalid Landmark Format";
      let description = "Mobile numbers are not allowed in landmark field. Use the mobile field instead.";
      
      switch (violation.violationStage) {
        case 'banned':
          title = "Account Suspended";
          description = "Your account has been suspended due to repeated violations. Contact support if you believe this is an error.";
          break;
          
        case 'flagged':
          title = "Account Flagged";
          const flagDate = violation.flaggedUntil ? new Date(violation.flaggedUntil).toLocaleDateString('en-GB') : 'N/A';
          description = `Mobile numbers are not allowed in landmark field. Your account is now flagged until ${flagDate}. Further violations will result in account suspension. (Total violations: ${violation.violationCount})`;
          break;
          
        default: // 'normal'
          if (landmarkBlockResult.severity === 'violation') {
            title = "Content Violation";
            description = `Mobile numbers are not allowed in landmark field. This violation has been recorded. (Total violations: ${violation.violationCount})`;
          } else {
            title = "Content Warning";
            description = `Mobile numbers are not allowed in landmark field. Please follow our community guidelines.`;
          }
      }
      
      toast({
        title,
        description,
        variant: "destructive"
      });
      
    } catch (dbError) {
      console.error('Error recording violation to database:', dbError);
      
      // Fallback to local tracking if database fails
      const userStatus = violationTracker.recordViolation(currentUserId, landmarkBlockResult.severity);
      
      let title = "Invalid Landmark Format";
      let description = "Mobile numbers are not allowed in landmark field. Use the mobile field instead.";
      
      if (userStatus.isBanned) {
        title = "Account Suspended";
        description = "Your account has been suspended due to repeated violations. Contact support if you believe this is an error.";
      } else if (userStatus.isFlagged) {
        title = "Account Flagged";
        description = `${description} Your account is now flagged. Further violations will result in account suspension. (Warnings: ${userStatus.warnings}, Violations: ${userStatus.violations})`;
      } else if (landmarkBlockResult.severity === 'warning') {
        description = `${description} This is a warning - please follow our community guidelines.`;
      }
      
      toast({
        title,
        description,
        variant: "destructive"
      });
    }
    
    errors.push("Mobile numbers are not allowed in landmark field. Use the mobile field instead.");
  }
  
  // Validate the dedicated mobile field format (10 digits starting with 6-9)
  const mobileRegex = /^[6-9]\d{9}$/;
  if (address.mobile && !mobileRegex.test(address.mobile)) {
    errors.push("Please enter a valid 10-digit mobile number in the mobile field");
  }
  
  return errors;
};

  const isBuyer = role === "buyer";
  const isSeller = role === "seller";

  // UPDATED: Calculate payment breakdown with proper trust score logic and platform fee reduction
  const calculatePaymentBreakdown = async (buyerTrustScore: number, amount: number): Promise<PaymentBreakdown> => {
    const originalAmount = amount;
    
    // Get platform fee percentage based on buyer's trust score - ADDED
    const getPlatformFeePercent = (trustScore: number): number => {
      if (trustScore >= 5000) return 1;
      if (trustScore >= 3500) return 1.5;
      if (trustScore >= 2000) return 2;
      if (trustScore >= 1000) return 2.5;
      return 3;
    };
    
    const platformFeePercent = getPlatformFeePercent(buyerTrustScore);
    const platformFee = (originalAmount * platformFeePercent) / 100;
    
    // Calculate trust score discount - FIXED LOGIC
    const maxTrustDiscountAvailable = buyerTrustScore / 10; // Convert trust score to rupees
    const potentialTrustDiscountPercent = 10; // 10% of original amount
    const potentialTrustDiscountAmount = (originalAmount * potentialTrustDiscountPercent) / 100;
    
    // Apply the minimum of: available trust score discount OR 10% of original amount
    const discount = Math.min(maxTrustDiscountAvailable, potentialTrustDiscountAmount);
    
    const finalAmount = originalAmount + platformFee;
    
    return {
      originalAmount,
      discount,
      platformFee,
      platformFeePercent,
      finalAmount: applyDiscount ? finalAmount - discount : finalAmount,
      canApplyDiscount: discount > 0,
      maxTrustDiscountAvailable,
      trustScorePoints: buyerTrustScore
    };
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!deal || isUpdating) return;
    setIsUpdating(true);
  
    try {
    if (newStatus === "completed" && isBuyer) {
      setIsReleasingPayment(true);
      const scoreData = await getUserTrustScore(sellerId);
      const getFeeReduction = () => {
        if (!scoreData) return 0.08; // Default highest fee
        if (scoreData.score >= 5000) return 0.01;
        if (scoreData.score >= 3200) return 0.03;
        if (scoreData.score >= 2000) return 0.04;
        if (scoreData.score >= 1100) return 0.05;
        if (scoreData.score >= 600) return 0.07;
        return 0.08;
      };

      const commissionRate = getFeeReduction();
      const totalAmount = parseFloat(deal.amount);
      const platformFee = totalAmount * commissionRate;
      const sellerAmount = totalAmount - platformFee;

      try {
        // First update status to payment-processing
        await updateDealStatus(deal.$id, {
          status: "payment-processing",
          amount: deal.amount,
          requestedAmount: deal.requestedAmount || "0",
          commissionRate: commissionRate.toString(), // Store commission rate for webhook reference
          updatedAt: new Date().toISOString(),
        });

        // Call the payout function
        const payoutResponse = await functions.createExecution(
          appwriteConfig.cashfreeReleasePayment,
          JSON.stringify({
            dealId: deal.$id,
            sellerId: sellerId,
            sellerAmount: sellerAmount,
            buyerId: buyerId,
            // The payout function will generate transferId as: DEAL_{dealId}_{timestamp}
            // This format allows webhook to extract dealId easily
          })
        );

        // Parse the response
        let payoutResult;
        try {
          payoutResult = JSON.parse(payoutResponse.responseBody);
        } catch (parseError) {
          console.error("Failed to parse payout response:", parseError);
          throw new Error("Invalid payout response format");
        }

        if (payoutResult.success) {
          // Payout initiated successfully - webhook will handle final status update
          // Notify seller about payment processing
          notificationService.createNotification({
            title: "Payment Initiated",
            body: `Payment of ₹${sellerAmount} is being processed to your bank account. You'll receive another notification once the transfer is completed (typically 1-4 hours).`,
            type: "payment",
            recipient_id: sellerId,
            sender_id: "system",
          });

          // Notify buyer about payment processing
          notificationService.createNotification({
            title: "Payment Processing",
            body: `₹${totalAmount} has been released and is being transferred to the seller. The deal will be completed once the transfer is confirmed.`,
            type: "payment",
            recipient_id: buyerId,
            sender_id: "system",
          });

          // Show success toast
          toast({
            title: "Payment Initiated",
            description: "Payment is being processed. The deal will be completed automatically once the transfer is confirmed.",
          });

        } else {
          // Payout failed - revert status
          await updateDealStatus(deal.$id, {
            status: "completed",
            amount: deal.amount,
            requestedAmount: deal.requestedAmount || "0",
            updatedAt: new Date().toISOString(),
          });

          // Notify both parties about the failure
          notificationService.createNotification({
            title: "Payment Processing Failed",
            body: `Payment release failed: ${payoutResult.error}. Please contact support with Deal ID: ${deal.$id}`,
            type: "error",
            recipient_id: sellerId,
            sender_id: "system",
          });

          notificationService.createNotification({
            title: "Payment Processing Failed",
            body: `Payment release failed: ${payoutResult.error}. Please contact support with Deal ID: ${deal.$id}`,
            type: "error",
            recipient_id: buyerId,
            sender_id: "system",
          });

          // Show error toast
          toast({
            title: "Payment release failed!",
            description: `Payment release failed: ${payoutResult.error}`,
            variant: "destructive"
          });
        }

      } catch (paymentError: any) {
        console.error("Payment release failed:", paymentError);
        
        // Revert status on error
        await updateDealStatus(deal.$id, {
          status: "payment-processing",
          amount: deal.amount,
          requestedAmount: deal.requestedAmount || "0",
          updatedAt: new Date().toISOString(),
        });
        
        // Notify both parties about the technical failure
        notificationService.createNotification({
          title: "Payment Processing Error",
          body: `Technical error during payment release: ${paymentError.message}. Support has been notified.`,
          type: "error",
          recipient_id: sellerId,
          sender_id: "system",
        });

        notificationService.createNotification({
          title: "Payment Processing Error",
          body: `Technical error during payment release: ${paymentError.message}. Support has been notified.`,
          type: "error",
          recipient_id: buyerId,
          sender_id: "system",
        });

        toast({
          title: "Payment Processing Error",
          description: `Technical error during payment release: ${paymentError.message}. Support has been notified.`,
          variant: "destructive"
        });
      }
      
      setIsReleasingPayment(false);
      
    } else {
  // Handle other status updates normally
  await updateDealStatus(deal.$id, {
    status: newStatus,
    amount: deal.amount,
    requestedAmount: deal.requestedAmount || "0",
    updatedAt: new Date().toISOString(),
  });

  notificationService.createNotification({
    title: "Deal Update",
    body:
      newStatus === "delivered"
        ? "Item delivered successfully!"
        : newStatus === "completed"
        ? "Deal Completed Successfully!"
        : newStatus === "payment-processing"
        ? "Payment is being processed!"
        : "Dispute Raised!",
    type: "deal",
    recipient_id: isSeller ? buyerId : sellerId,
    sender_id: currentUserId,
  });
    }
    } catch (error) {
      console.error("Status update failed:", error);
      notificationService.createNotification({
        title: "Update Failed",
        body: "Failed to update deal status. Please try again.",
        type: "error",
        recipient_id: currentUserId,
        sender_id: "system",
      });
    } finally {
      setIsReleasingPayment(false);
      setIsUpdating(false);
    }
  };
  
  const handleDisputeSubmit = async () => {
    if (!deal || isUpdating || !disputeReason.trim()) return;
    setIsUpdating(true);
    try {
      await updateDealStatus(deal.$id, {
        status: "dispute-raised",
        disputeStatus: "pending",
        disputeReason,
        disputeRaisedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      notificationService.createNotification({
        title: "Dispute Raised",
        body: `Buyer has raised a dispute: ${disputeReason}`,
        type: "dispute",
        recipient_id: sellerId,
        sender_id: currentUserId,
      });

      notificationService.createNotification({
        title: "Deal Dispute Raised",
        body: `Dispute raised in deal ${deal.$id}: ${disputeReason}. Amount: ₹${deal.amount}`,
        type: "dispute", 
        recipient_id: "68575f4e000617f8b3f6",
        sender_id: currentUserId,
      });
      setShowDisputeForm(false);
      setDisputeReason("");
    } catch (err) {
      console.error("Dispute submission failed", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleResolveDispute = async () => {
    if (!deal || isUpdating) return;
    setIsUpdating(true);
    try {
      await updateDealStatus(deal.$id, {
        status: "delivered",
        disputeStatus: "resolved",
        updatedAt: new Date().toISOString(),
      });

      notificationService.createNotification({
        title: "Dispute Resolved",
        body: "The seller has resolved your dispute",
        type: "deal",
        recipient_id: buyerId,
        sender_id: sellerId,
      });
    } catch (err) {
      console.error("Failed to resolve dispute", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRequestPayment = async () => {
    if (!deal || isUpdating) return;
    const requestedAmount = prompt("Enter requested payment amount:");
    if (!requestedAmount) return;
    
    if (!/^\d*\.?\d+$/.test(requestedAmount)) {
        alert("Please enter a valid number");
        return;
    }

    setIsUpdating(true);
    try {
      await updateDealStatus(deal.$id, {
        requestedAmount,
        status: "payment-requested",
        updatedAt: new Date().toISOString(),
      });

      notificationService.createNotification({
        title: "Deal Update",
        body: isSeller ? "Payment requested!" : "Payment received!",
        type: "deal",
        recipient_id: isSeller ? buyerId : sellerId,
        sender_id: currentUserId,
      });
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  // UPDATED: Handle payment modal opening with new calculation
  const handlePaymentClick = async () => {
    if (!deal || !deal.requestedAmount) return;

    try {
      // Get buyer trust score
      const buyerTrustData = await getUserTrustScore(buyerId);
      
      const breakdown = await calculatePaymentBreakdown(
        buyerTrustData?.score || 0,
        parseFloat(deal.requestedAmount)
      );
      
      setPaymentBreakdown(breakdown);
      setShowPaymentDialog(true);
      // Always apply discount if available
      setApplyDiscount(true);
    } catch (error) {
      console.error("Error calculating payment breakdown:", error);
      toast({
        title: "Error",
        description: "Could not calculate payment details. Please try again.",
        variant: "destructive"
      });
    }
  };

  // New function to handle confirmed payment
  const handleConfirmPayment = async () => {
    if (!deal || !paymentBreakdown || isProcessing) return;

    // Check if Cashfree SDK is loaded
    if (!window.Cashfree) {
      toast({
        title: "Payment System Error",
        description: "Payment system is not ready. Please refresh the page.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);

    try {
      const cashfreeOrderIdTemp = `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Calculate buyer's final payment amount (with discount if applied)
      const buyerPaymentAmount = applyDiscount ? 
        paymentBreakdown.originalAmount + paymentBreakdown.platformFee - paymentBreakdown.discount :
        paymentBreakdown.finalAmount;

      // Create the Cashfree order with buyer's payment amount
      const orderResponse = await createDealPaymentOrder(
        deal.$id,
        buyerPaymentAmount, // Buyer pays this amount
        {
          id: buyerId,
          name: user.name,
          email: user.emailOrPhone,
          phone: user.emailOrPhone
        },
        deal.sellerId,
        `${window.location.origin}/payment/callback?order_id=${cashfreeOrderIdTemp}&source=deal&entity_id=${deal.$id}&buyer_id=${buyerId}&seller_id=${deal.sellerId}`
      );

      if (!orderResponse.success || !orderResponse.payment_session_id) {
        throw new Error(orderResponse.error || 'Failed to create payment session');
      }

      const cashfreeOrderId = orderResponse.order_id;

      if (!cashfreeOrderId) {
        throw new Error('No order ID received from Cashfree');
      }

      // Initialize payment
      const cashfree = new window.Cashfree({
        mode: 'production'
      });

      const checkoutOptions = {
        paymentSessionId: orderResponse.payment_session_id,
        returnUrl: `${window.location.origin}/payment/callback?order_id=${cashfreeOrderId}&source=deal&entity_id=${deal.$id}&buyer_id=${buyerId}&seller_id=${deal.sellerId}`,
        redirectTarget: '_modal'
      };

      // Close the dialog before opening payment
      setShowPaymentDialog(false);

      // Handle payment completion
      cashfree.checkout(checkoutOptions).then(async (response: any) => {
        try {
          if (response.error) {
            throw new Error(response.error.message || 'Payment failed');
          }

          if (response.paymentDetails) {
            const verificationOrderId = cashfreeOrderId;
            
            // Verify payment on our backend
            const isValid = await verifyCashfreePayment({
              orderId: verificationOrderId,
              paymentId: response.paymentDetails.paymentId
            });

            if (!isValid) {
              throw new Error("Payment verification failed");
            }

            // Update deal status with ORIGINAL amount (seller gets original amount)
            await updateDealStatus(deal.$id, {
              amount: paymentBreakdown.originalAmount.toString(), // Seller gets original amount
              status: "paid",
              cashfreeOrderId: cashfreeOrderId,
              cashfreePaymentId: response.paymentDetails.paymentId,
              paymentVerifiedAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });

            // If discount was applied, deduct points from buyer's trust score
            if (applyDiscount && paymentBreakdown.discount > 0) {
              try {
                const pointsUsed = Math.round(paymentBreakdown.discount * 10);
                
                await logTrustScoreEvent(
                  buyerId,
                  'discount_used',
                  -pointsUsed,
                  `Discount of ₹${paymentBreakdown.discount.toFixed(2)} (${pointsUsed} pts) used for deal payment`,
                  deal.$id
                );

                console.log(`Trust score updated: ${pointsUsed} points deducted for discount usage`);
              } catch (trustScoreError) {
                console.error("Failed to update trust score:", trustScoreError);
              }
            }

            // Notify seller with original amount
            await notificationService.createNotification({
              title: "Payment Received",
              body: `₹${paymentBreakdown.originalAmount} secured in escrow`,
              type: "payment",
              recipient_id: deal.sellerId,
              sender_id: buyerId,
            });

            toast({
              title: "Payment Successful",
              description: `₹${buyerPaymentAmount.toFixed(2)} paid successfully${applyDiscount ? ` (Saved ₹${paymentBreakdown.discount.toFixed(2)})` : ''}`,
            });

            navigate(`/chats/${deal.sellerId}`);
          }
        } catch (error) {
          console.error("Payment processing failed:", error);
          
          await notificationService.createNotification({
            title: "Payment Error",
            body: "Contact support for assistance",
            type: "error",
            recipient_id: buyerId,
            sender_id: "system",
          });
          
          toast({
            title: "Payment Verification Failed",
            description: "Our team will review your payment shortly",
            variant: "destructive"
          });
        }
      }).catch((error: any) => {
        console.error("Cashfree checkout error:", error);
        toast({
          title: "Payment Failed",
          description: error.message || "Payment could not be completed",
          variant: "destructive"
        });
      });

    } catch (error) {
      console.error("Payment failed:", error);
      
      toast({
        title: "Payment Failed",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive"
      });
      
      notificationService.createNotification({
        title: "Payment Failed",
        body: error instanceof Error ? error.message : "Please try again",
        type: "error",
        recipient_id: buyerId,
        sender_id: "system",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading || !deal) return null;

  const showReviseButton = 
  isBuyer && 
  deal.status === "paid" && 
  !deal.deliveryAddress;

  const showDisputeInfo = deal.status === "dispute-raised" && deal.disputeReason;
  const showResolveButton = 
    isSeller && 
    deal.status === "dispute-raised" && 
    deal.disputeStatus === "pending";
    
  const showDisputeButton = 
    deal.status === "delivered" && 
    isBuyer && 
    (timeLeft === null || timeLeft > 0) &&
    deal.disputeStatus !== "pending";

  const showCompletionButton = 
    isBuyer && 
    deal.status !== "completed" && 
    deal.status !== "payment-processing" && (
      (deal.status === "delivered" && !deal.disputeStatus) ||
      (deal.disputeStatus === "resolved")
    );
  const handleDeliveryAddressSubmit = async () => {
    if (!deal || isSubmittingAddress) return;

    // Validate required fields
    if (!deliveryAddress.address || !deliveryAddress.mobile) {
      toast({
        title: "Missing Information",
        description: "Address and mobile number are required",
        variant: "destructive"
      });
      return;
    }
        setIsSubmittingAddress(true);
    // Validate for email
    const validationErrors = await validateDeliveryAddress(deliveryAddress);
    if (validationErrors.length > 0) {
      toast({
        title: "Invalid Information",
        description: validationErrors[0],
        variant: "destructive"
      });
      setIsSubmittingAddress(false);
      return;
    }
    
    try {
      await updateDealStatus(deal.$id, {
        deliveryAddress: JSON.stringify(deliveryAddress),
        status: "delivery-details-added",
        updatedAt: new Date().toISOString(),
      });
      
      await notificationService.createNotification({
        title: "Delivery Address Added",
        body: "Buyer has provided delivery address. Check deal tracker for details.",
        type: "deal",
        recipient_id: sellerId,
        sender_id: buyerId,
      });
      
      setShowDeliveryModal(false);
      
      toast({
        title: "Address Added",
        description: "Delivery address has been shared with the seller",
      });
      
    } catch (error) {
      console.error("Failed to add delivery address:", error);
      toast({
        title: "Error",
        description: "Failed to add delivery address. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmittingAddress(false);
    }
  };
  return (
    <>
      <div className="p-3 rounded-2xl bg-white shadow-md mb-4 text-sm md:text-base flex flex-col gap-3">
        {isCollapsible && (
          <div
            className="flex items-center justify-between cursor-pointer mb-2"
            onClick={() => setIsCollapsed(!isCollapsed)}>
            <h3 className="font-medium text-blue-800 dark:text-blue-200">
              Deal Tracker
            </h3>
            <svg
              className={`w-5 h-5 text-blue-600 dark:text-blue-300 transition-transform duration-200 ${
                isCollapsed ? "rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        )}
        {!isCollapsed && (
          <div>
            <div className="text-gray-800 font-semibold text-base md:text-lg">
              🧾 Deal Progress Tracker
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Status:</span>
              <span className="capitalize font-medium text-blue-600">
                {deal.status === "dispute-raised" &&
                deal.disputeStatus === "resolved"
                  ? "dispute-resolved"
                  : deal.status === "payment-processing"
                    ? "payment-processing"
                    : deal.status}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-gray-700">Amount:</span>
              <span className="font-semibold text-green-600">
                ₹{deal.amount || 0}
              </span>
            </div>

            {showDisputeInfo && (
              <>
                <div className="text-rose text-sm mt-1">
                  Dispute Reason: {deal.disputeReason}
                </div>
                <div className="text-rose text-xs mt-1">
                  Evidence: Please share in the chat.
                </div>
                {deal.disputeRaisedAt && (
                  <div className="text-gray-500 text-xs mt-1">
                    Raised at: {new Date(deal.disputeRaisedAt).toLocaleString()}
                  </div>
                )}
              </>
            )}

            {(deal.status === "negotiation" ||
              deal.status === "payment-requested") &&
              parseFloat(deal.requestedAmount) > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Requested:</span>
                  <span className="text-orange-600 font-medium">
                    ₹{deal.requestedAmount} (INR)
                  </span>
                </div>
              )}

            {deal.status === "delivered" &&
              isBuyer &&
              timeLeft !== null &&
              timeLeft > 0 &&
              deal.disputeStatus !== "pending" && (
                <div className="text-sm text-gray-600">
                  ⏳ Time left to raise dispute:{" "}
                  {Math.floor(timeLeft / (1000 * 60 * 60))}h{" "}
                  {Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60))}m
                </div>
              )}

            <div className="flex flex-wrap gap-2 justify-end mt-2">
              {deal.status === "negotiation" && isSeller && (
                <button
                  onClick={handleRequestPayment}
                  disabled={isUpdating}
                  className="bg-yellow-500 text-white px-4 py-1.5 rounded-md hover:bg-yellow-600 transition disabled:opacity-50">
                  {isUpdating ? "Processing..." : "🧾 Request Payment"}
                </button>
              )}

              {deal.status === "payment-requested" && isBuyer && (
                <button
                  onClick={handlePaymentClick}
                  disabled={isUpdating}
                  className="bg-blue-500 text-white px-4 py-1.5 rounded-md hover:bg-blue-600 transition disabled:opacity-50">
                  {isUpdating
                    ? "Processing..."
                    : `💸 Pay ₹${deal.requestedAmount} (INR)`}
                </button>
              )}
              {deal.isDealRevised && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Deal Status:</span>
                  <span className="text-purple-600 font-medium text-sm">
                    🔄 Revised (Premium)
                  </span>
                </div>
              )}

              {showReviseButton && (
                <button
                  onClick={checkReviseEligibility}
                  disabled={isCheckingTrustScore}
                  className="bg-purple-500 text-white px-4 py-1.5 rounded-md hover:bg-purple-600 transition disabled:opacity-50 flex items-center gap-1">
                  {isCheckingTrustScore ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Checking...</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      <span> Revise Deal</span>
                    </>
                  )}
                </button>
              )}
              {deal.status === "delivery-details-added" && isSeller && (
                <button
                  onClick={() => handleStatusUpdate("delivered")}
                  disabled={isUpdating}
                  className="bg-green-500 text-white px-4 py-1.5 rounded-md hover:bg-green-600 transition disabled:opacity-50">
                  {isUpdating ? "Processing..." : "📦 Mark as Delivered"}
                </button>
              )}

              {deal.status === "paid" && isBuyer && !deal.deliveryAddress && (
                <button
                  onClick={() => setShowDeliveryModal(true)}
                  disabled={isUpdating}
                  className="bg-blue-500 text-white px-4 py-1.5 rounded-md hover:bg-blue-600 transition disabled:opacity-50">
                  📍 Add Delivery Address
                </button>
              )}
              {deal.status === "delivery-details-added" &&
                deal.deliveryAddress && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">
                      📍 Delivery Address
                    </h4>
                    {(() => {
                      try {
                        const address = JSON.parse(deal.deliveryAddress);
                        return (
                          <div className="text-sm space-y-1 text-gray-700">
                            <div>
                              <strong>Address:</strong> {address.address}
                            </div>
                            {address.landmark && (
                              <div>
                                <strong>Landmark:</strong> {address.landmark}
                              </div>
                            )}

                            {isSeller && (
                              <div className="mt-2 p-2 bg-yellow-100 rounded text-yellow-800">
                                <strong>Delivery contact: </strong> We
                                coordinate between the seller's delivery agent
                                and the buyer.
                              </div>
                            )}
                            {isBuyer && (
                              <div>
                                {address.mobile && (
                                  <div>
                                    <strong>Mobile:</strong> {address.mobile}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      } catch (e) {
                        return (
                          <div className="text-rose-500">
                            Error loading address
                          </div>
                        );
                      }
                    })()}
                  </div>
                )}

              {showDisputeButton && (
                <button
                  onClick={() => setShowDisputeForm(true)}
                  disabled={isUpdating}
                  className="bg-white text-rose border border-rose-500 px-4 py-1.5 rounded-md hover:bg-slate-100 transition">
                  🚩 Raise Dispute
                </button>
              )}

              {showCompletionButton && (
                <button
                  onClick={() => handleStatusUpdate("completed")}
                  disabled={isUpdating}
                  className="bg-purple-500 text-white px-4 py-1.5 rounded-md hover:bg-purple-600 transition disabled:opacity-50">
                  {isUpdating ? "Processing..." : "✅ Confirm Completion"}
                </button>
              )}

              {showResolveButton && (
                <button
                  onClick={handleResolveDispute}
                  disabled={isUpdating}
                  className="bg-green-500 text-white px-4 py-1.5 rounded-md hover:bg-green-600 transition disabled:opacity-50">
                  {isUpdating ? "Processing..." : "✅ Resolve Dispute"}
                </button>
              )}

              {/* Payment Processing Status Display */}
              {deal.status === "payment-processing" && (
                <div className="mt-2 p-2 bg-orange-50 rounded-md w-full">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-green-600 font-semibold">
                        ✔️ Deal Completed
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-orange-600 font-semibold">
                        ⏳ Payment Processing
                      </span>
                      <span className="text-sm text-gray-500">
                        (1-4 hours to seller's bank)
                      </span>
                      {isReleasingPayment && (
                        <span className="text-sm text-gray-500">
                          (Initiating...)
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      You'll be notified when payment is completed
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Dispute Form */}
      {showDisputeForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-4 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">Raise Dispute</h3>
              <button onClick={() => setShowDisputeForm(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
              placeholder="Explain the issue and provide any evidence..."
              className="w-full border rounded-md p-2 mb-4 h-32"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDisputeForm(false)}
                className="px-4 py-2 border rounded-md hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleDisputeSubmit}
                disabled={!disputeReason.trim() || isUpdating}
                className="px-4 py-2 bg-rose-500 text-white rounded-md hover:bg-rose-600 disabled:opacity-50">
                {isUpdating ? "Submitting..." : "Submit Dispute"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Breakdown Dialog - UPDATED UI */}
      {showPaymentDialog && paymentBreakdown && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                Payment Details
              </h2>
              <button
                onClick={() => setShowPaymentDialog(false)}
                className="p-1 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Deal Info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <h3 className="font-medium text-gray-900 mb-1">Deal Payment</h3>
                <p className="text-sm text-gray-600">Requested by seller</p>
              </div>

              {/* Payment Breakdown */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Payment Breakdown
                </h4>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Requested Amount</span>
                    <span className="text-gray-900 font-medium">
                      ₹{paymentBreakdown.originalAmount.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600 flex items-center">
                      <Shield className="w-3 h-3 mr-1" />
                      Platform Fee ({paymentBreakdown.platformFeePercent}%)
                    </span>
                    <span className="text-green-600 font-medium">
                      ₹{paymentBreakdown.platformFee.toFixed(2)}
                    </span>
                  </div>

                  <div className="border-t pt-2">
                    <div className="flex justify-between font-medium">
                      <span>Subtotal</span>
                      <span>
                        ₹
                        {(
                          paymentBreakdown.originalAmount +
                          paymentBreakdown.platformFee
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {paymentBreakdown.canApplyDiscount && (
                    <div className="border-t pt-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-blue-600 flex items-center">
                          <Wallet className="w-3 h-3 mr-1" />
                          Trust Score Discount
                        </span>
                        <span className="font-medium text-blue-600">
                          -₹{paymentBreakdown.discount.toFixed(2)}
                        </span>
                      </div>

                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={applyDiscount}
                          onChange={(e) => setApplyDiscount(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">
                          Apply 10% discount cap (
                          {Math.round(paymentBreakdown.discount * 10)} points
                          will be deducted)
                        </span>
                      </label>
                    </div>
                  )}

                  <div className="border-t pt-2 flex justify-between font-semibold text-lg">
                    <span>Total Amount</span>
                    <span>
                      ₹
                      {(applyDiscount
                        ? paymentBreakdown.finalAmount -
                          paymentBreakdown.discount
                        : paymentBreakdown.finalAmount
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Trust Score Info */}
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="flex items-center mb-2">
                  <Wallet className="w-4 h-4 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-900">
                    Trust Score Benefits
                  </span>
                </div>
                <p className="text-xs text-blue-700 mb-1">
                  Your trust score: {paymentBreakdown.trustScorePoints} |
                  Available discount: ₹
                  {paymentBreakdown.maxTrustDiscountAvailable.toFixed(2)}
                </p>
                <p className="text-xs text-blue-700">
                  Platform fee: {paymentBreakdown.platformFeePercent}% | Higher
                  trust scores = lower fees!
                </p>
              </div>

              {/* Escrow Notice */}
              <div className="bg-yellow-50 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  <Shield className="w-3 h-3 inline mr-1" />
                  Your payment is secured in escrow until delivery is confirmed.
                </p>
              </div>

              {/* Warning if discount not applied */}
              {paymentBreakdown.canApplyDiscount && !applyDiscount && (
                <div className="bg-rose-50 rounded-lg p-3">
                  <p className="text-xs text-rose-800 flex items-start">
                    <AlertCircle className="w-3 h-3 inline mr-1 mt-0.5 flex-shrink-0" />
                    <span>
                      You have unused trust score points! Applying the discount
                      will save you ₹{paymentBreakdown.discount.toFixed(2)}.
                    </span>
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex space-x-3">
              <button
                onClick={() => setShowPaymentDialog(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleConfirmPayment}
                disabled={isProcessing}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors flex items-center justify-center">
                {isProcessing ? (
                  <>
                    <Loader />
                    <span className="ml-2">Processing...</span>
                  </>
                ) : (
                  `Pay ₹${(applyDiscount
                    ? paymentBreakdown.finalAmount - paymentBreakdown.discount
                    : paymentBreakdown.finalAmount
                  ).toFixed(2)}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Address Modal */}
      {showDeliveryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                Add Delivery Address
              </h2>
              <button
                onClick={() => setShowDeliveryModal(false)}
                className="p-1 text-gray-700 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="bg-yellow-50 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  📍 Provide complete address with city, state, pincode.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Complete Address *
                </label>
                <textarea
                  value={deliveryAddress.address}
                  onChange={(e) =>
                    setDeliveryAddress({
                      ...deliveryAddress,
                      address: e.target.value,
                    })
                  }
                  className="w-full border text-gray-700 rounded-md p-2 text-sm h-20"
                  placeholder="House/Flat no, Street, Area, City, State, Pincode"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  value={deliveryAddress.mobile}
                  onChange={(e) =>
                    setDeliveryAddress({
                      ...deliveryAddress,
                      mobile: e.target.value,
                    })
                  }
                  className="w-full border text-gray-700 rounded-md p-2 text-sm"
                  placeholder="10-digit mobile number"
                  maxLength={10}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Landmark
                </label>
                <input
                  type="text"
                  value={deliveryAddress.landmark}
                  onChange={(e) =>
                    setDeliveryAddress({
                      ...deliveryAddress,
                      landmark: e.target.value,
                    })
                  }
                  className="w-full text-gray-700 border rounded-md p-2 text-sm"
                  placeholder="Nearby landmark (Optional)"
                />
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50 flex space-x-3">
              <button
                onClick={() => setShowDeliveryModal(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleDeliveryAddressSubmit}
                disabled={isSubmittingAddress}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors">
                {isSubmittingAddress ? "Saving..." : "Save Address"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReviseDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-50 to-blue-50">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Revise Deal
                </h2>
                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                  Premium
                </span>
              </div>
              <button
                onClick={() => setShowReviseDialog(false)}
                className="p-1 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content - Only upgrade prompt */}
            <div className="p-4 space-y-4">
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <span className="text-amber-800 font-medium">
                    Premium Feature Required
                  </span>
                </div>
                <p className="text-amber-700 text-sm">
                  Your trust score: {buyerTrustScore || 0} points
                  <br />
                  Required: 5,000 points
                </p>
              </div>

              <div className="space-y-3">
                <h3 className="font-medium text-gray-900">
                  Upgrade to Premium and get:
                </h3>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <RefreshCw className="w-4 h-4 text-purple-500 mt-0.5" />
                    <span>
                      <strong>Deal Revision:</strong> Renegotiate price after
                      payment but before shipment.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-purple-500 mt-0.5" />
                    <span>
                      <strong>Busiconn Agent Support:</strong> Priority dispute, Instant refund
                      resolution
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Wallet className="w-4 h-4 text-purple-500 mt-0.5" />
                    <span>
                      <strong>Credits:</strong> Pay now, and get the same amount free for your future purchases!
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CreditCard className="w-4 h-4 text-purple-500 mt-0.5" />
                    <span>
                      <strong>Lower Fees:</strong> Just 1% platform fee
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-3">
                <p className="text-purple-800 text-sm font-medium">
                  💎 Buy {5000 - (buyerTrustScore || 0)} more points to unlock
                  premium features
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex space-x-3">
              <button
                onClick={() => setShowReviseDialog(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={() => navigate("/trust-score")}
                className="flex-1 py-2 px-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-md hover:from-purple-700 hover:to-blue-700 transition-colors flex items-center justify-center gap-2">
                <Crown className="w-4 h-4" />
                <span>Buy Points</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};